import Icon from '@/components/UI/Icon/Icon';
import Tooltip from '@/components/UI/Tooltip';
import { _isEmpty, _notNil } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type {
  DataTableCellIndexCoordinate,
  DataTableMetadataColumn,
} from '@/components/UI/DataTableV2/DataTable.model';
import { parseSelectValue } from '@/components/UI/DataTableV2/DataTable.util';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellEditability } from '../hooks/useCellEditability';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';
import tableStyles from '../DataTableV2.module.scss';
import styles from './MetadataSelectCell.module.scss';

interface MetadataSelectCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

/**
 * Pure display cell for metadata select / multi_select columns.
 *
 * Renders the current value as chips (or a placeholder when empty) plus a
 * dropdown chevron affordance when the cell is editable. Editing is owned by the
 * centralized `SelectCellEditor` shell: clicking the chevron selects the cell and
 * activates the selection (the same path double-click uses), which mounts the
 * editor. This component never opens its own dropdown or writes cell values.
 */
export const MetadataSelectCell: FC<MetadataSelectCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle, contentStyle } = useCellPositioning(positionOffset, false);
  const { isEditable } = useCellEditability(coordinate);

  const column = dataTableService.columnByIndex(coordinate.column) as DataTableMetadataColumn | undefined;
  const glossaryApiId = column?.metadata?.metadata_glossary_api_id;
  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';
  const hasOptions = _notNil(glossaryApiId);

  const displayOptions = useMemo(() => {
    return parseSelectValue(cellData?.value, glossaryApiId).map((value) => ({ value, label: value }));
  }, [cellData?.value, glossaryApiId]);

  const handleIconClick = useCallback(() => {
    dataTableService.selectCell(coordinate);
    dataTableService.activateSelection();
  }, [dataTableService, coordinate]);

  const chips = useMemo(() => {
    if (_isEmpty(displayOptions)) {
      return (
        <span className={styles.placeholder} data-test-element="placeholder">
          &nbsp;
        </span>
      );
    }

    return displayOptions.map((option) => (
      <span key={option.value} className={styles.chip} data-test-element="chip">
        {option.label}
      </span>
    ));
  }, [displayOptions]);

  return (
    <div
      className={cn(tableStyles.CellContainer, { [tableStyles.groupBorder]: hasGroupBorder })}
      style={containerStyle}
      data-test-component="MetadataSelectCell"
      data-test-element="container"
      data-test-key={`(${coordinate.column},${coordinate.row})`}
      data-cell-coordinate={`${coordinate.column},${coordinate.row}`}
    >
      {cellState === 'Loading' ? (
        <div className={cn(tableStyles.Cell, tableStyles.Loading)} data-test-element="loading-cell" />
      ) : (
        <Tooltip render={tooltip}>
          <div
            className={cn(tableStyles.Cell, tableStyles[selectionState], tableStyles[cellState])}
            style={contentStyle}
            data-state={cellState}
            data-test-element="cell"
          >
            <div className={styles.container}>
              <div className={styles.valuesContainer}>{chips}</div>
              {isEditable && hasOptions && (
                <div className={styles.dropdownIcon} onClick={handleIconClick} data-test-element="dropdown-icon">
                  <Icon icon="chev_down" width={10} height={10} />
                </div>
              )}
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
